import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    listNotes,
    createNote as createNoteAPI,
    getNote as getNoteAPI,
    updateNote as updateNoteAPI,
    deleteNote as deleteNoteAPI,
} from '../services/notesService';

const NotesContext = createContext(null);

export function NotesProvider({ children }) {
    // ── Global notes state ──────────────────────────────────────────────
    const [notes, setNotes] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [currentNote, setCurrentNote] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // ── Panel state ─────────────────────────────────────────────────────
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [panelNoteId, setPanelNoteId] = useState(null);
    const [panelInitialContent, setPanelInitialContent] = useState(null);

    // ── CRUD helpers ────────────────────────────────────────────────────

    const fetchNotes = useCallback(async (params = {}) => {
        try {
            setIsLoading(true);
            setError(null);
            const res = await listNotes(params);
            const data = res?.data || res;
            setNotes(data?.notes || []);
            setPagination(data?.pagination || null);
        } catch (err) {
            console.error('[NotesContext] fetchNotes:', err);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchNoteById = useCallback(async (noteId) => {
        try {
            setIsLoading(true);
            setError(null);
            const res = await getNoteAPI(noteId);
            const data = res?.data || res;
            setCurrentNote(data);
            return data;
        } catch (err) {
            console.error('[NotesContext] fetchNoteById:', err);
            setError(err);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const createNote = useCallback(async (payload) => {
        try {
            setError(null);
            const res = await createNoteAPI(payload);
            const data = res?.data || res;
            // Prepend to list
            setNotes(prev => [{ ...payload, _id: data.noteId, ...data }, ...prev]);
            return data;
        } catch (err) {
            console.error('[NotesContext] createNote:', err);
            setError(err);
            throw err;
        }
    }, []);

    const updateNote = useCallback(async (noteId, payload) => {
        try {
            setError(null);
            const res = await updateNoteAPI(noteId, payload);
            const data = res?.data || res;
            // Update in list
            setNotes(prev => prev.map(n => n._id === noteId ? { ...n, ...payload, ...data } : n));
            if (currentNote && currentNote._id === noteId) {
                setCurrentNote(prev => ({ ...prev, ...payload, ...data }));
            }
            return data;
        } catch (err) {
            console.error('[NotesContext] updateNote:', err);
            setError(err);
            throw err;
        }
    }, [currentNote]);

    const deleteNoteFn = useCallback(async (noteId) => {
        try {
            setError(null);
            await deleteNoteAPI(noteId);
            setNotes(prev => prev.filter(n => n._id !== noteId));
            if (currentNote && currentNote._id === noteId) {
                setCurrentNote(null);
            }
        } catch (err) {
            console.error('[NotesContext] deleteNote:', err);
            setError(err);
            throw err;
        }
    }, [currentNote]);

    // ── Panel helpers ───────────────────────────────────────────────────

    /** Open the note editor panel for an existing or new note */
    const openNotePanel = useCallback((noteId = null, initialContent = null) => {
        setPanelNoteId(noteId);
        setPanelInitialContent(initialContent);
        setIsPanelOpen(true);
    }, []);

    const closeNotePanel = useCallback(() => {
        setIsPanelOpen(false);
        setPanelNoteId(null);
        setPanelInitialContent(null);
    }, []);

    // ── Context value ───────────────────────────────────────────────────

    const value = {
        // State
        notes,
        pagination,
        currentNote,
        isLoading,
        error,
        // CRUD
        fetchNotes,
        fetchNoteById,
        createNote,
        updateNote,
        deleteNote: deleteNoteFn,
        // Panel
        isPanelOpen,
        panelNoteId,
        panelInitialContent,
        openNotePanel,
        closeNotePanel,
    };

    return <NotesContext.Provider value={value}>{children}</NotesContext.Provider>;
}

export function useNotes() {
    const ctx = useContext(NotesContext);
    if (!ctx) throw new Error('useNotes must be used within <NotesProvider>');
    return ctx;
}

export default NotesContext;
